import { Download } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/site/motion";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const CSV = "/transaction-import-template.csv";
const HEAD = ["Date", "Type", "Account", "To Account", "Amount", "Check#", "Payee", "Category", "Fund", "Memo"];
const ROWS = [
  ["2026-06-07", "income", "*1234", "", "1420.10", "", "", "Deposit", "General Fund", "Sunday offering"],
  ["2026-06-03", "expense", "*1234", "", "144.76", "4035", "Frontier Communications", "Utilities", "General Fund", "Internet/Phone Service"],
  ["2026-06-15", "transfer", "*1234", "*5678", "500.00", "", "", "", "", "Move to savings"],
];

const CsvPreview = () => (
  <div className="mt-5" data-testid="faq-csv-preview">
    <div className="overflow-x-auto border border-slate-200">
      <table className="w-max min-w-full text-left font-mono text-xs">
        <thead className="bg-navy text-white">
          <tr>{HEAD.map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody>
          {ROWS.map((r) => (
            <tr key={r[0] + r[1]} className="border-t border-slate-200 bg-white">
              {r.map((c, i) => <td key={HEAD[i]} className="whitespace-nowrap px-3 py-2 text-slate-700">{c || "\u2014"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <a href={CSV} download className="btn-outline mt-5" data-testid="faq-download-csv-button">
      <Download className="h-4 w-4" /> Download the CSV template
    </a>
  </div>
);

const ITEMS = [
  {
    id: "setup",
    q: "How do I set up Grace Treasury for my church?",
    a: (
      <>
        <p>Start in Settings. Add each of your church's bank accounts under <strong>Settings &rarr; Bank Accounts</strong>, then create your funds under <strong>Settings &rarr; Funds</strong> with their opening balances.</p>
        <p className="mt-3">Funds can be nested beneath a parent, such as a fund placed under the General Fund, and the parent's balance rolls up its children.</p>
      </>
    ),
  },
  {
    id: "import",
    q: "Can I bring in past months of transactions?",
    a: (
      <p>Yes. The CSV import dialog bulk-loads past months, so your records are complete from the first day. Prepare one row per transaction in a spreadsheet, save it as CSV, and import it.</p>
    ),
  },
  {
    id: "csv-format",
    q: "How should I organise my CSV file?",
    a: (
      <>
        <p>Use the same columns as the template below. A few notes on the example rows:</p>
        <ul className="mt-3 list-disc space-y-1.5 pl-5">
          <li><strong>Date</strong> is written as year-month-day, e.g. 2026-06-07.</li>
          <li><strong>Type</strong> is income, expense or transfer.</li>
          <li><strong>Account</strong> identifies the bank account, e.g. *1234. <strong>To Account</strong> is only filled in for transfers.</li>
          <li><strong>Amount</strong> is a plain number, e.g. 144.76.</li>
          <li><strong>Check#</strong>, <strong>Payee</strong>, <strong>Category</strong>, <strong>Fund</strong> and <strong>Memo</strong> can be left blank where they don't apply.</li>
        </ul>
        <CsvPreview />
      </>
    ),
  },
  {
    id: "bank-connection",
    q: "Does Grace Treasury connect to our bank?",
    a: (
      <>
        <p>No. Grace Treasury is a simple accounting and reporting tool with no direct bank connection, so transactions are never pulled in or matched automatically.</p>
        <p className="mt-3">Your team enters transactions or imports them from a CSV file, then reconciles each account against the bank statement by marking entries as cleared. Nothing changes in your books unless someone on your team records it.</p>
      </>
    ),
  },
  {
    id: "backups",
    q: "How are backups handled?",
    a: (
      <p>Settings &rarr; Backup &amp; Restore offers a manual export and restore at any time, automatic nightly backups, and optional email backups.</p>
    ),
  },
  {
    id: "hosting",
    q: "Do we have to host it ourselves?",
    a: (
      <>
        <p>No. You can choose either option:</p>
        <ul className="mt-3 list-disc space-y-1.5 pl-5">
          <li><strong>Hosted for you</strong>, for a fee.</li>
          <li><strong>Self-hosted</strong> on a system your church runs. If you'd like help, the developer can handle the installation for you, on-site or remotely, for a fee.</li>
        </ul>
        <p className="mt-3">Pricing for both is still being finalised. <Link to="/pricing" className="text-navy underline underline-offset-4" data-testid="faq-pricing-link">Join the waitlist</Link> to hear it first.</p>
      </>
    ),
  },
];

export const Faq = () => (
  <section className="bg-white py-20 sm:py-28 lg:py-32" data-testid="faq-section">
    <div className="container-x grid gap-12 lg:grid-cols-12 lg:gap-16">
      <Reveal className="lg:col-span-4">
        <p className="eyebrow">For treasurers</p>
        <h2 className="h2 mt-4">Questions before you begin.</h2>
        <p className="mt-5 text-base leading-relaxed text-slate-600">Setting up, bringing in your history, keeping your records safe, and where Grace Treasury runs.</p>
      </Reveal>
      <Reveal delay={0.08} className="min-w-0 lg:col-span-8">
        <Accordion type="single" collapsible className="border-t border-slate-200">
          {ITEMS.map((it) => (
            <AccordionItem key={it.id} value={it.id} className="border-b border-slate-200">
              <AccordionTrigger className="py-6 text-left font-display text-xl font-semibold text-navy hover:no-underline sm:text-2xl" data-testid={`faq-trigger-${it.id}`}>
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-base leading-relaxed text-slate-600" data-testid={`faq-content-${it.id}`}>
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);
